import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, PackageCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RMOrders() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState([]);
  const [filter, setFilter] = useState('ordered');
  const [showForm, setShowForm] = useState(false);
  const [receivingId, setReceivingId] = useState(null);
  const [receiveWeight, setReceiveWeight] = useState('');
  const [gradeOptions, setGradeOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const [formData, setFormData] = useState({
    steel_grade: '', section: '', qty_weight: '', vendor: '',
    order_date: new Date().toISOString().split('T')[0], expected_date: '', remarks: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = filter === 'all' ? '' : `?status=${filter}`;
      const [o, s, items, vendors] = await Promise.all([
        axios.get(`${API}/rm-orders/${params}`),
        axios.get(`${API}/rm-orders/summary`),
        axios.get(`${API}/masters/items`),
        axios.get(`${API}/masters/vendors`),
      ]);
      setOrders(o.data);
      setSummary(s.data);
      // Unique steel grades & sections from item master, case-insensitive dedupe, sorted
      const gradeMap = new Map();
      const sectionMap = new Map();
      (items.data || []).forEach(it => {
        const g = (it.steel_grade || '').trim();
        const sec = (it.section || '').trim();
        if (g && !gradeMap.has(g.toUpperCase())) gradeMap.set(g.toUpperCase(), g);
        if (sec && !sectionMap.has(sec.toUpperCase())) sectionMap.set(sec.toUpperCase(), sec);
        (it.alternate_sections || []).forEach(alt => {
          const a = (alt || '').trim();
          if (a && !sectionMap.has(a.toUpperCase())) sectionMap.set(a.toUpperCase(), a);
        });
      });
      setGradeOptions([...gradeMap.values()].sort((a, b) => a.localeCompare(b)));
      setSectionOptions([...sectionMap.values()].sort((a, b) => a.localeCompare(b)));
      // Vendors filtered by vendor_type === 'steel' (case-insensitive)
      const steelVendors = (vendors.data || []).filter(v => (v.vendor_type || '').trim().toLowerCase() === 'steel');
      setVendorOptions(steelVendors.sort((a, b) => (a.vendor_name || a.vendor_id).localeCompare(b.vendor_name || b.vendor_id)));
    } catch (err) { console.error(err); }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/rm-orders/`, formData);
      toast.success('RM order created');
      setShowForm(false);
      setFormData({ steel_grade: '', section: '', qty_weight: '', vendor: '', order_date: new Date().toISOString().split('T')[0], expected_date: '', remarks: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleReceive = async (id) => {
    if (!receiveWeight || parseFloat(receiveWeight) <= 0) { toast.error('Enter valid weight'); return; }
    try {
      const res = await axios.put(`${API}/rm-orders/${id}/receive`, { received_weight: parseFloat(receiveWeight) });
      toast.success(res.data.message);
      setReceivingId(null); setReceiveWeight('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this order?')) return;
    try { await axios.delete(`${API}/rm-orders/${id}`); toast.success('Deleted'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ordered': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'partial': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'received': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="rm-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="rm-orders-title">RM ORDER TRACKING</h1>
          <p className="text-sm text-text-muted mt-2">Track raw material orders against requirements</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium" data-testid="toggle-form">
          <Plus className="w-4 h-4 mr-2" />{showForm ? 'CANCEL' : 'NEW ORDER'}
        </Button>
      </div>

      {/* Summary: Required vs Available vs Ordered */}
      {summary.length > 0 && (
        <div className="bg-white border border-border rounded-sm">
          <div className="p-3 border-b border-border bg-gray-50/50">
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-800">RM Requirement vs Availability</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b">
                <th className="h-9 px-3 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Grade</th>
                <th className="h-9 px-3 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Section</th>
                <th className="h-9 px-3 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Required (kg)</th>
                <th className="h-9 px-3 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Available (kg)</th>
                <th className="h-9 px-3 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Ordered (kg)</th>
                <th className="h-9 px-3 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Net Gap (kg)</th>
              </tr></thead>
              <tbody>
                {summary.map((s, i) => (
                  <tr key={i} className="border-b hover:bg-muted/50">
                    <td className="p-3 font-mono text-xs">{s.steel_grade}</td>
                    <td className="p-3 font-mono text-xs">{s.section}</td>
                    <td className="p-3 text-right font-mono text-xs">{s.required.toLocaleString()}</td>
                    <td className="p-3 text-right font-mono text-xs">{s.available.toLocaleString()}</td>
                    <td className="p-3 text-right font-mono text-xs">{s.ordered_pending.toLocaleString()}</td>
                    <td className={`p-3 text-right font-mono text-xs font-bold ${s.net_gap > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {s.net_gap > 0 ? `+${s.net_gap.toLocaleString()}` : s.net_gap.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-white border border-border shadow-sm p-6">
          <h2 className="text-2xl font-bold uppercase mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>NEW RM ORDER</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Steel Grade</Label>
              <Select value={formData.steel_grade} onValueChange={(v) => setFormData({...formData, steel_grade: v})}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="rm-grade"><SelectValue placeholder="Select steel grade..." /></SelectTrigger>
                <SelectContent>
                  {gradeOptions.length === 0 ? (
                    <SelectItem value="__none" disabled>No grades in item master</SelectItem>
                  ) : gradeOptions.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Section</Label>
              <Select value={formData.section} onValueChange={(v) => setFormData({...formData, section: v})}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="rm-section"><SelectValue placeholder="Select section..." /></SelectTrigger>
                <SelectContent>
                  {sectionOptions.length === 0 ? (
                    <SelectItem value="__none" disabled>No sections in item master</SelectItem>
                  ) : sectionOptions.map(sec => <SelectItem key={sec} value={sec}>{sec}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity (kg)</Label>
              <Input type="number" step="0.01" value={formData.qty_weight} onChange={e => setFormData({...formData, qty_weight: e.target.value})} required className="mt-1.5 rounded-none" data-testid="rm-qty" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Vendor</Label>
              <Select value={formData.vendor} onValueChange={(v) => setFormData({...formData, vendor: v})}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="rm-vendor"><SelectValue placeholder="Select steel vendor..." /></SelectTrigger>
                <SelectContent>
                  {vendorOptions.length === 0 ? (
                    <SelectItem value="__none" disabled>No vendors with type = steel</SelectItem>
                  ) : vendorOptions.map(v => (
                    <SelectItem key={v.vendor_id} value={v.vendor_name || v.vendor_id}>
                      {v.vendor_name || v.vendor_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Order Date</Label>
              <Input type="date" value={formData.order_date} onChange={e => setFormData({...formData, order_date: e.target.value})} required className="mt-1.5 rounded-none" data-testid="rm-order-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Expected Date</Label>
              <Input type="date" value={formData.expected_date} onChange={e => setFormData({...formData, expected_date: e.target.value})} className="mt-1.5 rounded-none" data-testid="rm-expected-date" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Remarks</Label>
              <Input value={formData.remarks} onChange={e => setFormData({...formData, remarks: e.target.value})} className="mt-1.5 rounded-none" data-testid="rm-remarks" />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm w-full" data-testid="submit-rm-order">CREATE ORDER</Button>
            </div>
          </form>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2">
        {['ordered', 'partial', 'received', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm border transition-all ${filter === f ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-text-muted border-border hover:border-primary/50'}`}>
            {f}
          </button>
        ))}
      </div>

      {/* Orders table */}
      <div className="bg-white border border-border shadow-sm">
        {orders.length === 0 ? (
          <div className="p-12 text-center text-text-muted">No {filter !== 'all' ? filter : ''} RM orders</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b">
                <th className="h-10 px-3 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Order Date</th>
                <th className="h-10 px-3 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Grade</th>
                <th className="h-10 px-3 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Section</th>
                <th className="h-10 px-3 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Vendor</th>
                <th className="h-10 px-3 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Ordered</th>
                <th className="h-10 px-3 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Received</th>
                <th className="h-10 px-3 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Pending</th>
                <th className="h-10 px-3 text-center font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Status</th>
                <th className="h-10 px-3 text-center font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Action</th>
              </tr></thead>
              <tbody>
                {orders.map((o, idx) => (
                  <tr key={o.id} className="border-b hover:bg-muted/50" data-testid={`rm-order-row-${idx}`}>
                    <td className="p-3 font-mono text-xs">{new Date(o.order_date).toLocaleDateString()}</td>
                    <td className="p-3 font-mono text-xs">{o.steel_grade}</td>
                    <td className="p-3 font-mono text-xs">{o.section}</td>
                    <td className="p-3 text-xs">{o.vendor}</td>
                    <td className="p-3 text-right font-mono text-xs">{o.qty_weight} kg</td>
                    <td className="p-3 text-right font-mono text-xs">{o.received_weight || 0} kg</td>
                    <td className="p-3 text-right font-mono text-xs font-semibold">
                      <span className={o.pending_weight > 0 ? 'text-amber-700' : 'text-emerald-600'}>{o.pending_weight} kg</span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${getStatusBadge(o.status)}`}>{o.status}</span>
                    </td>
                    <td className="p-3 text-center">
                      {o.status !== 'received' && (
                        receivingId === o.id ? (
                          <div className="flex items-center gap-1 justify-center">
                            <Input type="number" step="0.01" value={receiveWeight} onChange={e => setReceiveWeight(e.target.value)}
                              placeholder="kg" className="w-20 h-7 text-xs rounded-none" autoFocus data-testid={`receive-weight-${idx}`} />
                            <button onClick={() => handleReceive(o.id)} className="p-1 hover:bg-green-100 text-green-600 rounded-sm" data-testid={`confirm-receive-${idx}`}>
                              <PackageCheck className="w-4 h-4" />
                            </button>
                            <button onClick={() => { setReceivingId(null); setReceiveWeight(''); }} className="p-1 hover:bg-red-100 text-red-500 rounded-sm text-xs font-bold">x</button>
                          </div>
                        ) : (
                          <button onClick={() => { setReceivingId(o.id); setReceiveWeight(''); }}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm text-xs font-semibold hover:bg-emerald-100"
                            data-testid={`receive-btn-${idx}`}>
                            <PackageCheck className="w-3 h-3" />RECEIVE
                          </button>
                        )
                      )}
                      {isAdmin && o.status !== 'received' && (
                        <button onClick={() => handleDelete(o.id)} className="ml-1 p-1 hover:bg-red-50 text-red-400 hover:text-red-600 rounded-sm" data-testid={`delete-order-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
